import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import Image from 'utils/image';
import ProductPrice from '../utils/product-price';
import Swatches from './swatches';
import SizeSelector from './size-selector';
import ProductBadges from './product-badges';
import ProductSubBrand from '../product/product-subbrand';
import { trackAddToCart } from '../utils/tracking';
import { slugify } from '../utils/helpers';
import DiscountTag from '../discount-tag';
import { useCartContext } from '../cart/cart-context';

const ProductCard = ({ product, placement, inputPrefix, onSale, collectionTitle, collectionId }) => {
  const [currentVariant, setCurrentVariant] = useState(product.variants[0]);
  const [isAdded, setIsAdded] = useState(false);
  const [quickAddVisible, setQuickAddVisible] = useState(false);
  const { pathname } = window.location;
  const { discountTagMessage, discountTagBgColor, discountTagMessageColor } = product.metafields;
  const isCollectionsPage = pathname.includes('/collections/');
  const vendor = product.vendor === 'Thinx for All Leaks' ? 'tfal' : product.vendor.toLowerCase();
  const dataTestid = isCollectionsPage ? `${vendor}-shop-all-product-card` : null;
  const showSizeSelectorInline = placement === 'cart' || placement === 'upsell';
  const shouldShowDiscountTag = placement !== 'cart' && placement !== 'upsell' && discountTagMessage;
  const compareAtPrice = currentVariant.compareAtPrice?.amount || 0;
  const price = currentVariant.price.amount;
  const discountValue = compareAtPrice > price ? compareAtPrice - price : 0;
  const { cart } = useCartContext();

  const productOptions =
    product?.optionsWithValues !== undefined
      ? product?.optionsWithValues
      : product?.variantBySelectedOptions?.selectedOptions;

  const sizeOptionIndex = productOptions?.findIndex((o) => o.name.toLowerCase().includes('size'));

  const handleVariantChange = (variantId) => {
    const variant = product.variants.find((v) => v.id === variantId) || product.variants[0];
    setCurrentVariant(variant);
  };
  const handleClick = (event) => {
    if (onSale === 'true') {
      localStorage.setItem('lastVisitedCollection', onSale);
    } else {
      localStorage.removeItem('lastVisitedCollection');
    }

    const allCards = Array.from(document.querySelectorAll('article'));
    const card = event.currentTarget.closest('article');
    const index = allCards.indexOf(card);

    let itemListId = '';
    let itemListName = '';

    if (pathname === '/') {
      itemListId = 'homepage';
      itemListName = 'Homepage';
    } else if (isCollectionsPage) {
      itemListId = collectionId;
      itemListName = collectionTitle;
    } else if (pathname.includes('/products/')) {
      itemListId = 'related_products';
      itemListName = 'Related Products';
    } else if (pathname.includes('/search')) {
      itemListId = 'search_results';
      itemListName = 'Search Results';
    } else if (pathname.includes('/cart')) {
      itemListId = 'cart';
      itemListName = 'Cart';
    }

    // GA4 select_item event
    window.runIfTargetingConsent(() => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items: [
            {
              item_id: currentVariant.sku || currentVariant.id,
              item_name: product.title,
              discount: discountValue,
              index,
              item_brand: product.vendor,
              item_category: product.collections?.[0] || '',
              item_category2: product.collections?.[1] || '',
              item_category3: product.collections?.[2] || '',
              item_category4: product.collections?.[3] || '',
              item_category5: product.collections?.[4] || '',
              item_variant: currentVariant.title,
              price: currentVariant.price.amount,
              quantity: 1,
            },
          ],
        },
      });
    });
  };

  useEffect(() => {
    window.addEventListener('plp:filtered', (e) => handleFilterChange(e.detail));

    return () => {
      window.removeEventListener('plp:filtered', (e) => handleFilterChange(e.detail));
    };
  }, []);

  const handleFilterChange = ({ appliedFilterOptions: filters }) => {
    if (product.hasOnlyDefaultVariant || filters?.length === 0) return;
    const colorFilters = filters
      .filter((filter) => {
        return filter.paramName === 'filter.v.option.color';
      })
      ?.map((filter) => filter.value);
    const matchingVariant = product.variants.find((variant) => {
      const colorValue = variant.selectedOptions?.find((option) => option.name.toLowerCase() === 'color')?.value;
      const colorValueByGroup = colorValue
        ? window.availableColors?.find((color) => slugify(color.name) === slugify(colorValue))?.group
        : null;
      return colorFilters.includes(colorValue) || colorFilters.includes(colorValueByGroup);
    });

    if (matchingVariant) handleVariantChange(matchingVariant.id);
  };

  const handleAddToCart = (variantId) => {
    const matchingCartItem = cart.state.items?.find((item) => {
      const currentVariantId = Number(variantId.match(/[^/]+$/)[0]);
      return currentVariantId === item.variant_id;
    });

    const quantityInCart = matchingCartItem?.quantity || 0;

    const variant = product.variants.find((v) => v.id === variantId) || product.variants[0];
    setCurrentVariant(variant);

    const inventoryQuantity = variant.inventoryQuantity - 10;

    const availableInventoryToAdd = inventoryQuantity - quantityInCart;

    const quantityToAdd = availableInventoryToAdd < 1 ? 0 : 1;

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          merchandiseId: variantId,
          quantity: quantityToAdd,
        },
      },
    });

    document.dispatchEvent(addToCartEvent);

    setIsAdded(true);

    const allCards = Array.from(document.querySelectorAll('article'));
    const card = document.querySelector(`[data-test-id="${dataTestid}"]`);
    const index = allCards.indexOf(card);

    window.runIfTargetingConsent(() => {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: variant.price?.currencyCode || 'USD',
          value: variant.price.amount * 1,
          items: [
            {
              item_id: variant.sku || variant.id,
              item_name: product.title,
              affiliation: '',
              coupon: '',
              discount: discountValue,
              index,
              item_brand: product.vendor,
              item_category: product.collections?.[0] || '',
              item_category2: product.collections?.[1] || '',
              item_category3: product.collections?.[2] || '',
              item_category4: product.collections?.[3] || '',
              item_category5: product.collections?.[4] || '',
              item_variant: variant.title,
              location_id: '',
              price: variant.price.amount,
              quantity: 1,
            },
          ],
        },
      });
    });

    trackAddToCart({
      product,
      variant,
      quantity: quantityToAdd,
    });

    setTimeout(() => {
      setIsAdded(false);
      setQuickAddVisible(false);
      if (placement === 'cart') {
        document.querySelector('[data-cart-data]').scrollTo(0, 0);
      }
    }, 1000);
  };
  const QuickAdd = () => {
    return isAdded ? (
      <div className="button button--primary w-full !bg-default !text-inverse">{window.TMW.Strings.product.added}</div>
    ) : (
      <SizeSelector
        product={product}
        handleVariantChange={handleAddToCart}
        currentVariant={currentVariant}
        placement={placement}
        inline={showSizeSelectorInline}
      />
    );
  };

  return (
    <article
      className="relative overflow-hidden rounded"
      data-test-id={dataTestid}
    >
      <div
        className="group relative mb-2"
        role="group"
        aria-label="product-card"
      >
        {!showSizeSelectorInline && (
          <ProductBadges
            product={product}
            currentVariant={currentVariant}
            onSale={onSale}
          />
        )}

        <a
          className="group/image relative block no-underline"
          href={currentVariant.url}
          onClick={handleClick}
        >
          {currentVariant.image?.src ? (
            <>
              <Image
                {...currentVariant.image}
                srcWidths={{ sm: 640 }}
                classes={clsx(
                  'w-full aspect-square image object-cover mx-auto',
                  currentVariant?.secondaryImage && 'group-hover/image:hidden',
                )}
              />

              {currentVariant?.secondaryImage && (
                <Image
                  {...currentVariant.secondaryImage}
                  alt={`${product.title} view 2`}
                  srcWidths={{ sm: 640 }}
                  classes="w-full aspect-square hidden group-hover/image:block image object-cover mx-auto"
                />
              )}
            </>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: product.placeholder_svg }} />
          )}
        </a>
        {!showSizeSelectorInline && (
          <div
            className="group/sizes quick-add-wrapper absolute bottom-0 w-full p-3 opacity-0 transition-opacity duration-300 group-hover:lg:block group-hover:lg:opacity-100 has-[:focus]:lg:opacity-100"
            role="tooltip"
          >
            <QuickAdd />
          </div>
        )}
      </div>

      <div className="relative h-5.5">
        <ProductSubBrand
          product={product}
          className="absolute left-0 top-0 mb-0.5"
        />
        {shouldShowDiscountTag && (
          <DiscountTag
            className="absolute right-0 top-0"
            message={discountTagMessage}
            bgColor={discountTagBgColor}
            textColor={discountTagMessageColor}
          />
        )}
      </div>

      <div
        className={clsx('mb-2 flex', {
          '[&>p]:body-3-bold flex-col justify-start': showSizeSelectorInline,
          'items-center justify-between gap-2': !showSizeSelectorInline && placement !== 'carousel',
          'flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-2': placement === 'carousel',
        })}
      >
        <h3>
          <a
            className={clsx('block', {
              'body-1-bold': !showSizeSelectorInline,
              'body-3-bold': showSizeSelectorInline,
            })}
            href={currentVariant.url}
            onClick={handleClick}
          >
            {product.title}
          </a>
        </h3>
        <ProductPrice
          product={product}
          currentVariant={currentVariant}
        />
      </div>
      <Swatches
        product={product}
        currentVariant={currentVariant}
        handleVariantChange={handleVariantChange}
        inputPrefix={inputPrefix}
        onSale={onSale}
      />
      {showSizeSelectorInline && (
        <div
          className="mt-3"
          role="status"
        >
          {quickAddVisible ? (
            <QuickAdd />
          ) : sizeOptionIndex >= 0 ? (
            <button
              onClick={() => setQuickAddVisible(true)}
              className="button button--secondary w-full px-3"
              data-test-id="quick-add"
            >
              {window.TMW.Strings.cart.quickAdd}
            </button>
          ) : (
            <button
              onClick={() => handleAddToCart(currentVariant.id)}
              className="button button--secondary w-full px-3"
            >
              {window.TMW.Strings.cart.addToCart}
            </button>
          )}
        </div>
      )}
    </article>
  );
};

export default ProductCard;
